import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logocleo from '../assets/logocleo.png';

function EsteticaHeader({ setCurrentPage }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const handleNavigationClick = (page) => {
    setCurrentPage(page);
    setIsMobileMenuOpen(false);
    // También vuelve arriba si se toca la sección en la que ya estás
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navItems = [
    { id: 'home', label: 'Inicio' },
    { id: 'faciales', label: 'Faciales' },
    { id: 'corporales', label: 'Corporales' },
    { id: 'masajes', label: 'Masajes' },
    { id: 'contacto', label: 'Contacto' }
  ];

  // Animaciones
  const logoVariants = {
    hover: {
      scale: 1.03,
      transition: { duration: 0.3 }
    }
  };

  const navItemVariants = {
    hover: {},
    tap: {
      scale: 0.97
    }
  };

  const underlineVariants = {
    hidden: { width: 0 },
    visible: { width: '100%' }
  };

  const mobileMenuVariants = {
    hidden: { 
      opacity: 0,
      height: 0,
      y: -20
    },
    visible: {
      opacity: 1,
      height: 'auto',
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        when: "beforeChildren",
        staggerChildren: 0.05
      }
    },
    exit: {
      opacity: 0,
      height: 0,
      y: -20,
      transition: {
        duration: 0.2
      }
    }
  };

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-crema/90 backdrop-blur-md z-50 border-b border-green-900/10">
      <nav className="container mx-auto px-6 py-3 flex justify-between items-center">
        {/* Logo y nombre */}
        <motion.div 
          className="flex items-center cursor-pointer"
          onClick={() => handleNavigationClick('home')}
          whileHover="hover"
          initial={false}
        >
          <motion.div variants={logoVariants}>
            <img 
              src={logocleo} 
              alt="Logo Cleo Estética" 
              className="w-14 h-14 object-contain"
            />
          </motion.div>
          <motion.h1 
            className="text-3xl font-semibold text-green-600 ml-3 font-serif italic"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            Estética
          </motion.h1>
        </motion.div>
        
        {/* Menú de escritorio */}
        <div className="hidden md:flex space-x-1">
          {navItems.map((item) => (
            <motion.div
              key={item.id}
              className="relative"
              onHoverStart={() => setHoveredItem(item.id)}
              onHoverEnd={() => setHoveredItem(null)}
              variants={navItemVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <button 
                onClick={() => handleNavigationClick(item.id)}
                className={`px-4 py-2 transition-colors text-[13px] uppercase tracking-[0.15em] relative group ${
                  hoveredItem === item.id ? 'text-green-800' : 'text-gray-600'
                }`}
              >
                {item.label}
                <motion.span
                  className="absolute bottom-0 left-0 h-px bg-green-700"
                  variants={underlineVariants}
                  initial="hidden"
                  animate={hoveredItem === item.id ? "visible" : "hidden"}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Botón de menú móvil */}
        <motion.div 
          className="md:hidden"
          whileTap={{ scale: 0.9 }}
        >
          <button 
            onClick={toggleMobileMenu}
            className="p-2 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
            aria-label="Menú"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </motion.div>
      </nav>

      {/* Menú móvil desplegable */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={mobileMenuVariants}
            className="md:hidden bg-crema shadow-lg overflow-hidden"
          >
            <div className="flex flex-col items-stretch px-6 py-2 space-y-1">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavigationClick(item.id)}
                  variants={mobileItemVariants}
                  className="px-4 py-3 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors text-base font-medium text-left rounded-md flex items-center"
                >
                  <motion.span 
                    className="w-1.5 h-1.5 bg-green-600 rounded-full mr-3"
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  />
                  {item.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default EsteticaHeader;